import { useCallback, useMemo } from "react";
import type { ExportDetailOptions } from "../types";
import { useMultiSiteContext } from "@hooks/use-site-context";
import { useScreenAlert } from "@hooks/use-screen-alert";
import { useUserContext } from "@hooks/use-auth-context";
import { useRefreshTokenMutation } from "@hooks/use-refresh-token-mutation";
import { fetchWithTokenRefresh } from "@hooks/shared/fetch-with-token-refresh";
import { exportDetailAssetsToCsv } from "@hooks/use-get/csv-builder";
import type { DetailExportFilterSet } from "@hooks/use-get/csv-builder";
import { isDateField } from "./date-utils";

/**
 * Returns true for any row field value that means "no data stored" on the asset:
 * empty string, "-", or common placeholder words (case-insensitive).
 * Used both to detect when to skip a server filter and in the post-fetch
 * client filter that keeps only assets with the same blank state.
 */
const isBlankFilterValue = (val: string): boolean =>
  val === "" || /^(-|unknown|n\/a|none|unspecified)$/i.test(val);

/** Resolves a dot-path string against an object, e.g. "meta_box.asset_site" */
const getNestedValue = (obj: any, path: string): string => {
  const value = path.split(".").reduce((acc: any, key: string) => {
    if (acc === null || acc === undefined) return undefined;
    if (typeof acc === "string" && acc.startsWith("{")) {
      try { return JSON.parse(acc)[key]; } catch { return undefined; }
    }
    return acc[key];
  }, obj);
  if (value === null || value === undefined) return "";
  if (typeof value === "object" && (value as any).name) return String((value as any).name);
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};


/**
 * Builds WP REST API date range query params — same format used by useGetAssets.
 * e.g. { purchase_date: "2024-01-01,2024-12-31", purchase_date_compare: "BETWEEN" }
 */
const buildDateRangeParams = (
  column: string,
  start: string | null,
  end: string | null,
): Record<string, string> => {
  const params: Record<string, string> = {};
  if (start && end) {
    params[column] = `${start},${end}`;
    params[`${column}_compare`] = "BETWEEN";
  } else if (start) {
    params[column] = start;
    params[`${column}_compare`] = ">";
  } else if (end) {
    params[column] = end;
    params[`${column}_compare`] = "<";
  }
  return params;
};

/**
 * Maps generic dimension field names to their dot-paths in the WP REST response.
 * Used for post-fetch filtering when a row field value is blank.
 */
const ROW_FIELD_TO_ASSET_PATH: Record<string, string> = {
  type_asset: "meta_box.type_asset",
  asset_site: "meta_box.asset_site",
  status_asset: "meta_box.status_asset",
  funding_source: "meta_box.funding_source",
};

/**
 * Maps global filter DB keys to their dot-paths in the WP REST response.
 * Used for post-fetch filtering when a global filter includes "null".
 */
const GLOBAL_FILTER_TO_ASSET_PATH: Record<string, string> = {
  asset_site: "meta_box.asset_site",
  type_asset: "meta_box.type_asset",
  status_asset: "meta_box.status_asset",
  funding_source: "meta_box.funding_source",
};

/**
 * Hook providing a paginated detail CSV export for report summary tables.
 *
 * For each selected summary row, pages through all matching assets from
 * wp-json/wp/v2/assets using the same column=value&column_compare= format
 * that useGetAssets uses. Deduplicates by asset ID across rows and downloads
 * a single CSV file.
 *
 * Global date-range filters are applied on every request. Multi-value array
 * global filters are applied only when a single value is selected (WP REST
 * does not support OR/IN); per-row dimension filters always take precedence.
 */
export const useExportDetailCsv = () => {
  const { getApiUrl, blogId } = useMultiSiteContext();
  const { setError } = useScreenAlert();
  const { user } = useUserContext();
  const { refreshUserToken } = useRefreshTokenMutation();
  const assetUrl = useMemo(() => getApiUrl("wp-json/wp/v2/assets"), [getApiUrl]);
  const ticketUrl = useMemo(() => getApiUrl("wp-json/wp/v2/ticket"), [getApiUrl]);
  const siteUsersUrl = useMemo(() => getApiUrl("wp-json/smassets/v1/site-users"), [getApiUrl]);

  const exportDetailCsv = useCallback(async ({
    selectedRows,
    exportCols,
    globalFilters,
    rowFilterFields,
    postType = "asset",
    filename = "export.csv",
  }: ExportDetailOptions) => {
    try {
      // --- Build one DetailExportFilterSet per selected row ----------------------
      // Each set maps WP REST query params (including _compare keys) to string values.
      // Date fields are expanded from YYYY / YYYY-MM shorthand to BETWEEN ranges.
      // Blank-value fields are omitted from the server query and captured for
      // client-side post-filtering instead.
      const filterSets: DetailExportFilterSet[] = [];
      const allBlankFieldPaths = new Set<string>();
      const rowsToProcess = selectedRows.length > 0 ? selectedRows : [null];

      for (const row of rowsToProcess) {
        const filterSet: DetailExportFilterSet = {};
        if (row && rowFilterFields?.length) {
          for (const field of rowFilterFields) {
            const rawVal = row[field];
            if (rawVal === undefined) continue;
            const strVal = String(rawVal).trim();

            if (isDateField(field, strVal)) {
              if (strVal === "") continue;
              let startDate: string, endDate: string;
              if (strVal.length === 7 && strVal.includes("-")) {
                const [year, month] = strVal.split("-");
                startDate = `${year}-${month}-01`;
                const nextMonth = parseInt(month) === 12 ? 1 : parseInt(month) + 1;
                const nextMonthYear = parseInt(month) === 12 ? parseInt(year) + 1 : parseInt(year);
                const lastDay = new Date(nextMonthYear, nextMonth - 1, 0).getDate();
                endDate = `${year}-${month}-${lastDay.toString().padStart(2, "0")}`;
              } else {
                startDate = `${strVal}-01-01`;
                endDate = `${strVal}-12-31`;
              }
              if (postType === "user") {
                filterSet[field] = `${startDate},${endDate}`;
              } else {
                filterSet[field] = `${startDate},${endDate}`;
                filterSet[`${field}_compare`] = "BETWEEN";
              }
            } else if (isBlankFilterValue(strVal)) {
              if (postType === "asset") {
                const assetPath = ROW_FIELD_TO_ASSET_PATH[field];
                if (assetPath) allBlankFieldPaths.add(assetPath);
              }
            } else {
              filterSet[field] = strVal;
              if (postType !== "user") {
                filterSet[`${field}_compare`] = "=";
              }
            }
          }
        }
        filterSets.push(filterSet);
      }

      // --- Build flat global filters (single-value exact matches + date ranges) --
      const flatGlobalFilters: Record<string, string> = {};
      for (const [columnName, value] of Object.entries(globalFilters)) {
        if (Array.isArray(value) && value.length > 0) {
          const hasNullValue = value.includes("null");
          const nonNullValues = value.filter((v: string) => v !== "null");
          if (hasNullValue) {
            if (postType === "asset") {
              const assetPath = GLOBAL_FILTER_TO_ASSET_PATH[columnName];
              if (assetPath) allBlankFieldPaths.add(assetPath);
            }
          }
          if (nonNullValues.length === 1) {
            flatGlobalFilters[columnName] = nonNullValues[0];
            if (postType !== "user") {
              flatGlobalFilters[`${columnName}_compare`] = "=";
            }
          }
        } else if (typeof value === "object" && value !== null && "start" in value && "end" in value) {
          const range = buildDateRangeParams(
            columnName,
            value.start ?? null,
            value.end ?? null,
          );
          if (postType === "user") {
            if (value.start) flatGlobalFilters[`${columnName}[start]`] = value.start;
            if (value.end) flatGlobalFilters[`${columnName}[end]`] = value.end;
          } else {
            Object.assign(flatGlobalFilters, range);
          }
        }
      }

      if (postType === "user" && blogId !== null) {
        flatGlobalFilters.blog_id = String(blogId);
      }

      // Post-filter: exclude assets that have a real (non-blank) value for any
      // field that was blank in the selected row (blank = "unknown", "-", etc.)
      const blankPaths = allBlankFieldPaths;
      const postFilter = blankPaths.size > 0
        ? (asset: any) => {
            return ![...blankPaths].some(path => !isBlankFilterValue(getNestedValue(asset, path)));
          }
        : undefined;

      // Map ExportColumnOption (label) → ExportColumn (header) for csv-builder
      const csvColumns = exportCols.map(c => ({ key: c.key, header: c.label }));
      const exportTitle = filename.replace(/\.csv$/i, "");

      const baseUrl = postType === "ticket" ? ticketUrl : postType === "user" ? siteUsersUrl : assetUrl;

      await exportDetailAssetsToCsv({
        baseUrl,
        filterSets,
        columns: csvColumns,
        exportTitle,
        postFilter: postType === "asset" ? postFilter : undefined,
        globalFilters: flatGlobalFilters,
        responseMode: postType === "user" ? "site-users" : "wp",
        fetcher: (url, init) =>
          fetchWithTokenRefresh(
            url,
            {
              method: init?.method ?? "GET",
              headers: {
                ...(init?.headers as Record<string, string> | undefined),
                ...(user?.authToken ? { Authorization: `Bearer ${user.authToken}` } : {}),
              },
            },
            () => refreshUserToken(user?.refreshToken ?? "")
          ),
      });
    } catch (err) {
      setError("Export failed: " + (err as Error)?.message);
    }
  }, [assetUrl, blogId, refreshUserToken, setError, siteUsersUrl, ticketUrl, user?.authToken, user?.refreshToken]);

  return { exportDetailCsv };
};
